import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Tag, 
  MoreVertical,
  Palette,
  ArrowUp,
  Pencil,
  RefreshCw,
  Loader2,
  LayoutGrid
} from 'lucide-react';
import { categoriesApi } from '@/api/categoriesApi';
import { useToast } from '@/hooks/useToast';
import { CategoryModal } from '@/components/categories/CategoryModal';
import type { Category } from '@/types/api';

interface CategoriesProps {
  embedded?: boolean;
}

export function Categories({ embedded = false }: CategoriesProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll(true),
  });

  const seedMutation = useMutation({
    mutationFn: categoriesApi.seedDefaults,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      success('Categories Added', 'Default categories have been added.', 5000);
    },
    onError: () => {
      error('Failed', 'Could not add default categories.');
    },
  });

  const handleSuccess = () => {
    success(
      editingCategory ? 'Category Updated' : 'Category Created',
      editingCategory 
        ? 'Category has been updated successfully.' 
        : 'New category has been added.',
      5000
    );
    setEditingCategory(null);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setShowModal(true);
    setActiveMenu(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {!embedded && (
          <div>
            <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-warm-cream">Design Families</h1>
            <p className="text-gray-600 dark:text-warm-sand mt-1">
              Manage design families (Duck, Panda, seasonal themes) used when creating products
            </p>
          </div>
        )}
        <div className={`flex gap-3 ${embedded ? 'sm:ml-auto' : ''}`}>
          <button 
            onClick={() => seedMutation.mutate()}
            disabled={seedMutation.isPending}
            className="btn-secondary"
          >
            {seedMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Reset Defaults
          </button>
          <button 
            onClick={() => setShowModal(true)}
            className="btn-primary"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Category
          </button>
        </div>
      </div>

      {/* Categories Grid */}
      {isLoading ? (
        <div className="card p-12 text-center">
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-amber-450/30 border-t-amber-450 rounded-full animate-spin" />
            <p className="text-gray-600 dark:text-warm-sand">Loading categories...</p>
          </div>
        </div>
      ) : categories?.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-charcoal-700 flex items-center justify-center">
              <LayoutGrid className="w-8 h-8 text-gray-600 dark:text-warm-sand" />
            </div>
            <div>
              <p className="text-gray-900 dark:text-warm-cream font-medium text-lg">No categories yet</p>
              <p className="text-gray-600 dark:text-warm-sand text-sm mt-1">
                Add your first category or reset to defaults
              </p>
            </div>
            <div className="flex gap-3 mt-2">
              <button 
                onClick={() => seedMutation.mutate()}
                className="btn-secondary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Add Defaults
              </button>
              <button 
                onClick={() => setShowModal(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories?.map((category, index) => (
            <div 
              key={category.id} 
              className={`card p-5 transition-all ${
                category.isActive ? 'hover:border-amber-450/30' : 'opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${category.color}20`, borderColor: `${category.color}40`, borderWidth: '1px' }}
                  >
                    <Tag className="w-6 h-6" style={{ color: category.color }} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-gray-900 dark:text-warm-cream">
                      {category.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      {category.isActive ? (
                        <span className="badge-success text-xs">Active</span>
                      ) : (
                        <span className="badge-danger text-xs">Inactive</span>
                      )}
                      <span className="text-xs text-gray-600 dark:text-warm-sand">Order: {category.sortOrder}</span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button 
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-lg transition-colors"
                    onClick={() => setActiveMenu(activeMenu === category.id ? null : category.id)}
                  >
                    <MoreVertical className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
                  </button>
                  
                  {activeMenu === category.id && (
                    <>
                      <div 
                        className="fixed inset-0 z-10"
                        onClick={() => setActiveMenu(null)}
                      />
                      <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg shadow-lg z-20 py-1">
                        <button
                          onClick={() => handleEdit(category)}
                          className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 flex items-center gap-2"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {category.description && (
                <p className="text-sm text-gray-600 dark:text-warm-sand mb-4 line-clamp-2">
                  {category.description}
                </p>
              )}

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-charcoal-700">
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-warm-taupe" />
                  <div 
                    className="w-6 h-6 rounded border border-gray-300 dark:border-charcoal-600"
                    style={{ backgroundColor: category.color }}
                  />
                </div>
                <div className="flex-1" />
                <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-warm-sand">
                  <ArrowUp className="w-3 h-3" />
                  <span>{index + 1}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category Modal */}
      <CategoryModal 
        isOpen={showModal} 
        onClose={handleCloseModal} 
        onSuccess={handleSuccess}
        category={editingCategory}
      />
    </div>
  );
}
